import React, { useRef, useEffect } from "react";
import { setupCanvas } from "./canvasUtils";

// ============================================
// Chapter 21  -  Lorentz Invariance Testing
// ============================================

/** 21.1 – CPT Theorem and Its Predictions */
export const CPTTheorem = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 340;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("CPT Theorem  -  Charge · Parity · Time", W / 2, 24);

    // Three symmetry boxes
    const ops = [
      { label: "C", name: "Charge\nConjugation", desc: "particle ↔ antiparticle", color: "#3B82F6", bg: "#DBEAFE" },
      { label: "P", name: "Parity\nInversion", desc: "r → −r (mirror)", color: "#8B5CF6", bg: "#EDE9FE" },
      { label: "T", name: "Time\nReversal", desc: "t → −t", color: "#10B981", bg: "#D1FAE5" },
    ];
    ops.forEach((o, i) => {
      const x = 40 + i * 180, y = 45, w = 150, h = 100;
      ctx.fillStyle = o.bg;
      ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.fill();
      ctx.strokeStyle = o.color; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.stroke();
      ctx.fillStyle = o.color; ctx.font = "bold 28px Inter,sans-serif"; ctx.textAlign = "center";
      ctx.fillText(o.label, x + w / 2, y + 35);
      ctx.font = "bold 11px Inter,sans-serif";
      o.name.split("\n").forEach((ln, li) => ctx.fillText(ln, x + w / 2, y + 55 + li * 14));
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(o.desc, x + w / 2, y + 90);
    });

    // Multiplication signs
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 20px Inter,sans-serif";
    ctx.fillText("�-", 200, 95); ctx.fillText("�-", 380, 95);

    // Combined CPT box
    ctx.fillStyle = "#FEF3C7";
    ctx.beginPath(); ctx.roundRect(80, 165, 400, 50, 10); ctx.fill();
    ctx.strokeStyle = "#F59E0B"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(80, 165, 400, 50, 10); ctx.stroke();
    ctx.fillStyle = "#92400E"; ctx.font = "bold 14px Inter,sans-serif";
    ctx.fillText("CPT combined: exact symmetry of any local QFT", W / 2, 185);
    ctx.fillStyle = "#78350F"; ctx.font = "12px Inter,sans-serif";
    ctx.fillText("Particles & antiparticles have identical masses, lifetimes, |charges|", W / 2, 205);

    // Predictions
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Key Predictions", W / 2, 240);
    const preds = [
      "m(e⁻) = m(e⁺)   -   verified to 10⁻⁹",
      "τ(K⁰) = τ(K̄⁰)   -   kaon lifetime equality",
      "g(p) = −g(p̄)   -   magnetic moment equality",
    ];
    ctx.font = "12px Inter,sans-serif"; ctx.fillStyle = "#475569";
    preds.forEach((p, i) => ctx.fillText(p, W / 2, 260 + i * 20));

    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("CPT violation would imply breakdown of locality or Lorentz invariance", W / 2, 325);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 21.2 – Lorentz Invariance as a Fundamental Symmetry */
export const LorentzSymmetry = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 340;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Lorentz Invariance  -  Foundation of Modern Physics", W / 2, 24);

    // Pillars
    const pillars = [
      { name: "Speed of light\nis constant c", icon: "⚡", color: "#F59E0B" },
      { name: "Laws same in\nall inertial frames", icon: "🔄", color: "#3B82F6" },
      { name: "No preferred\ndirection in space", icon: "🌐", color: "#8B5CF6" },
    ];
    pillars.forEach((p, i) => {
      const x = 45 + i * 175, y = 50, w = 150, h = 90;
      ctx.fillStyle = "#F8FAFC";
      ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.fill();
      ctx.strokeStyle = p.color; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.stroke();
      ctx.font = "22px serif"; ctx.fillStyle = "#1E293B";
      ctx.fillText(p.icon, x + w / 2, y + 28);
      ctx.fillStyle = p.color; ctx.font = "bold 11px Inter,sans-serif";
      p.name.split("\n").forEach((ln, li) => ctx.fillText(ln, x + w / 2, y + 50 + li * 14));
    });

    // Arrow down
    ctx.strokeStyle = "#1E293B"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(W / 2, 145); ctx.lineTo(W / 2, 165); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(W / 2, 165); ctx.lineTo(W / 2 - 5, 158); ctx.moveTo(W / 2, 165); ctx.lineTo(W / 2 + 5, 158); ctx.stroke();

    // Result box
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(60, 170, 440, 45, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(60, 170, 440, 45, 10); ctx.stroke();
    ctx.fillStyle = "#1E3A5F"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Lorentz Group: SO(3,1)  -  rotations + boosts", W / 2, 190);
    ctx.fillStyle = "#1E40AF"; ctx.font = "12px Inter,sans-serif";
    ctx.fillText("6 generators: 3 rotations J + 3 boosts K", W / 2, 207);

    // Consequences
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Physical Consequences", W / 2, 240);
    const cons = [
      "Time dilation & length contraction",
      "Energy-momentum relation: E² = (pc)² + (mc²)²",
      "Spin-statistics theorem (fermions/bosons)",
      "CPT symmetry of quantum field theory",
    ];
    ctx.font = "12px Inter,sans-serif"; ctx.fillStyle = "#475569";
    cons.forEach((co, i) => ctx.fillText(co, W / 2, 260 + i * 20));
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 21.3 – Standard Model Extension (SME) Framework */
export const SMEFramework = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Standard Model Extension (SME)", W / 2, 24);

    // SM Lagrangian box
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(30, 45, 230, 60, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 45, 230, 60, 10); ctx.stroke();
    ctx.fillStyle = "#1E40AF"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Standard Model", 145, 68);
    ctx.font = "12px Inter,sans-serif"; ctx.fillStyle = "#3B82F6";
    ctx.fillText("L_SM (Lorentz invariant)", 145, 88);

    // Plus sign
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 22px Inter,sans-serif";
    ctx.fillText("+", 290, 80);

    // SME correction box
    ctx.fillStyle = "#FEE2E2";
    ctx.beginPath(); ctx.roundRect(310, 45, 220, 60, 10); ctx.fill();
    ctx.strokeStyle = "#EF4444"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(310, 45, 220, 60, 10); ctx.stroke();
    ctx.fillStyle = "#991B1B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("SME Corrections", 420, 68);
    ctx.font = "12px Inter,sans-serif"; ctx.fillStyle = "#EF4444";
    ctx.fillText("δL (Lorentz-violating)", 420, 88);

    // Arrow
    ctx.strokeStyle = "#1E293B"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(W / 2, 110); ctx.lineTo(W / 2, 130); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(W / 2, 130); ctx.lineTo(W / 2 - 5, 123); ctx.moveTo(W / 2, 130); ctx.lineTo(W / 2 + 5, 123); ctx.stroke();

    // SME coefficient table
    ctx.fillStyle = "#F8FAFC";
    ctx.beginPath(); ctx.roundRect(40, 135, 480, 130, 10); ctx.fill();
    ctx.strokeStyle = "#94A3B8"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.roundRect(40, 135, 480, 130, 10); ctx.stroke();
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("SME Coefficients (Lorentz-violation parameters)", W / 2, 155);

    const coeffs = [
      { sym: "a_μ", sector: "CPT-odd, fermion", bound: "< 10⁻³¹ GeV" },
      { sym: "b_μ", sector: "CPT-odd, spin-coupled", bound: "< 10⁻³¹ GeV" },
      { sym: "c_μν", sector: "CPT-even, fermion", bound: "< 10⁻²⁷" },
      { sym: "k_F", sector: "CPT-even, photon", bound: "< 10⁻³²" },
    ];
    ctx.font = "bold 11px Inter,sans-serif";
    ["Symbol", "Sector", "Experimental bound"].forEach((h, i) => {
      ctx.fillStyle = "#64748B";
      ctx.fillText(h, 100 + i * 170, 175);
    });
    ctx.font = "12px Inter,sans-serif";
    coeffs.forEach((co, i) => {
      const y = 195 + i * 17;
      ctx.fillStyle = "#8B5CF6"; ctx.fillText(co.sym, 100, y);
      ctx.fillStyle = "#475569"; ctx.fillText(co.sector, 270, y);
      ctx.fillStyle = "#059669"; ctx.fillText(co.bound, 440, y);
    });

    // Bottom note
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Approach", W / 2, 290);
    ctx.font = "12px Inter,sans-serif"; ctx.fillStyle = "#475569";
    ctx.fillText("Add ALL possible Lorentz-violating operators to the SM Lagrangian", W / 2, 310);
    ctx.fillText("Then constrain their coefficients experimentally  -  most are < 10⁻²⁰", W / 2, 330);
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Developed by V.A. Kostelecký (Indiana U.)  -  hundreds of coefficients tabulated", W / 2, 350);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 21.4 – Precision Tests: Atomic Clocks and Particle Oscillations */
export const PrecisionTests = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Precision Tests of Lorentz Invariance", W / 2, 24);

    // Two main test categories
    const tests = [
      {
        title: "Atomic Clock Tests", x: 30, color: "#3B82F6", bg: "#DBEAFE",
        items: [
          "Compare orthogonal clocks",
          "as Earth rotates",
          "Sensitivity: δν/ν ~ 10⁻¹⁸",
          "Sidereal variation search",
        ],
      },
      {
        title: "Particle Oscillations", x: 300, color: "#8B5CF6", bg: "#EDE9FE",
        items: [
          "Neutral meson mixing",
          "K⁰-K̄⁰, B⁰-B̄⁰ systems",
          "CPT test via Δm, ΔΓ",
          "Sidereal modulation signal",
        ],
      },
    ];
    tests.forEach((t) => {
      ctx.fillStyle = t.bg;
      ctx.beginPath(); ctx.roundRect(t.x, 45, 230, 140, 10); ctx.fill();
      ctx.strokeStyle = t.color; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.roundRect(t.x, 45, 230, 140, 10); ctx.stroke();
      ctx.fillStyle = t.color; ctx.font = "bold 13px Inter,sans-serif";
      ctx.fillText(t.title, t.x + 115, 68);
      ctx.fillStyle = "#475569"; ctx.font = "12px Inter,sans-serif";
      t.items.forEach((it, i) => ctx.fillText(it, t.x + 115, 90 + i * 18));
    });

    // Sidereal signal diagram
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Sidereal Variation  -  Key Signature", W / 2, 210);
    ctx.strokeStyle = "#94A3B8"; ctx.lineWidth = 1;
    // Axes
    ctx.beginPath(); ctx.moveTo(80, 330); ctx.lineTo(480, 330); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(80, 330); ctx.lineTo(80, 230); ctx.stroke();
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Sidereal time (23h 56m period)", 280, 350);
    ctx.save(); ctx.translate(65, 280); ctx.rotate(-Math.PI / 2);
    ctx.fillText("Observable", 0, 0); ctx.restore();
    // Sine wave (signal of LV)
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath();
    for (let x = 80; x <= 480; x++) {
      const t = (x - 80) / 400;
      const y = 280 - 30 * Math.sin(t * 2 * Math.PI * 2);
      x === 80 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.stroke();
    // Labels
    ctx.fillStyle = "#3B82F6"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("LV signal: sinusoidal modulation", 350, 245);
    ctx.fillStyle = "#EF4444"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("No LV: flat (no sidereal dependence)", 350, 260);
    ctx.strokeStyle = "#EF4444"; ctx.lineWidth = 1.5; ctx.setLineDash([5, 4]);
    ctx.beginPath(); ctx.moveTo(80, 280); ctx.lineTo(480, 280); ctx.stroke();
    ctx.setLineDash([]);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 21.5 – Hughes-Drever Experiments and Modern Variants */
export const HughesDrever = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 340;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Hughes-Drever Experiment", W / 2, 24);

    // Concept: nuclear energy levels in rotating lab frame
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(30, 45, 240, 130, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 45, 240, 130, 10); ctx.stroke();
    ctx.fillStyle = "#1E40AF"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Original Idea (1960)", 150, 68);
    ctx.fillStyle = "#475569"; ctx.font = "12px Inter,sans-serif";
    const orig = [
      "Nuclear magnetic resonance",
      "of Li-7 nuclei in a magnetic field",
      "Look for shift as Earth rotates",
      "relative to 'fixed stars' frame",
    ];
    orig.forEach((l, i) => ctx.fillText(l, 150, 88 + i * 17));

    // Modern variants
    ctx.fillStyle = "#EDE9FE";
    ctx.beginPath(); ctx.roundRect(290, 45, 240, 130, 10); ctx.fill();
    ctx.strokeStyle = "#8B5CF6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(290, 45, 240, 130, 10); ctx.stroke();
    ctx.fillStyle = "#6D28D9"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Modern Variants", 410, 68);
    ctx.fillStyle = "#475569"; ctx.font = "12px Inter,sans-serif";
    const modern = [
      "Atom interferometers",
      "Trapped ion comparisons",
      "Optical cavity resonators",
      "Sensitivity: δ(m_inertial) < 10⁻²⁷",
    ];
    modern.forEach((l, i) => ctx.fillText(l, 410, 88 + i * 17));

    // Energy level diagram
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Isotropy Test: Energy Levels vs Orientation", W / 2, 200);
    // Horizontal energy levels
    const levColors = ["#3B82F6", "#8B5CF6", "#10B981"];
    [-1, 0, 1].forEach((m, i) => {
      const y = 225 + i * 25;
      ctx.strokeStyle = levColors[i]; ctx.lineWidth = 2.5;
      ctx.beginPath(); ctx.moveTo(120, y); ctx.lineTo(250, y); ctx.stroke();
      ctx.fillStyle = levColors[i]; ctx.font = "12px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(`m = ${m > 0 ? "+" : ""}${m}`, 260, y + 4);
    });
    ctx.textAlign = "center";
    // "Should remain degenerate"
    ctx.strokeStyle = "#F59E0B"; ctx.lineWidth = 1; ctx.setLineDash([4, 3]);
    ctx.beginPath(); ctx.moveTo(310, 225); ctx.lineTo(440, 230); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(310, 250); ctx.lineTo(440, 260); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(310, 275); ctx.lineTo(440, 265); ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = "#F59E0B"; ctx.font = "11px Inter,sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("If LV: splitting varies", 445, 248);
    ctx.fillText("with orientation", 445, 262);
    ctx.textAlign = "center";

    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Result: No anisotropy detected  -  Lorentz invariance holds to extraordinary precision", W / 2, 320);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 21.6 – ALPHA Antihydrogen Spectroscopy at CERN */
export const ALPHAAntihydrogen = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 340;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("ALPHA Experiment  -  Antihydrogen Spectroscopy", W / 2, 24);

    // Step diagram
    const steps = [
      { label: "1. Produce p̄", desc: "Antiproton\nDecelerator (AD)", color: "#EF4444", bg: "#FEE2E2" },
      { label: "2. Trap e⁺", desc: "Positron\naccumulation", color: "#8B5CF6", bg: "#EDE9FE" },
      { label: "3. Mix H̄", desc: "Combine in\nPenning trap", color: "#3B82F6", bg: "#DBEAFE" },
      { label: "4. Spectroscopy", desc: "Laser on 1S-2S\ntransition", color: "#10B981", bg: "#D1FAE5" },
    ];
    steps.forEach((s, i) => {
      const x = 20 + i * 135, y = 50, w = 120, h = 90;
      ctx.fillStyle = s.bg;
      ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.fill();
      ctx.strokeStyle = s.color; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.stroke();
      ctx.fillStyle = s.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.fillText(s.label, x + w / 2, y + 20);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      s.desc.split("\n").forEach((ln, li) => ctx.fillText(ln, x + w / 2, y + 42 + li * 14));
      if (i < 3) {
        ctx.strokeStyle = "#94A3B8"; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(x + w + 3, y + h / 2);
        ctx.lineTo(x + w + 12, y + h / 2); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(x + w + 12, y + h / 2);
        ctx.lineTo(x + w + 7, y + h / 2 - 4); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(x + w + 12, y + h / 2);
        ctx.lineTo(x + w + 7, y + h / 2 + 4); ctx.stroke();
      }
    });

    // Result comparison
    ctx.fillStyle = "#FEF3C7";
    ctx.beginPath(); ctx.roundRect(60, 160, 440, 70, 10); ctx.fill();
    ctx.strokeStyle = "#F59E0B"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(60, 160, 440, 70, 10); ctx.stroke();
    ctx.fillStyle = "#92400E"; ctx.font = "bold 14px Inter,sans-serif";
    ctx.fillText("1S–2S Transition Comparison", W / 2, 183);
    ctx.font = "12px Inter,sans-serif";
    ctx.fillText("Hydrogen:        f = 2 466 061 413 187 035 Hz", W / 2, 203);
    ctx.fillText("Antihydrogen:  agrees within 2 �- 10⁻¹² (ALPHA, 2020)", W / 2, 220);

    // CPT test summary
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("CPT & Lorentz Invariance Test", W / 2, 255);
    ctx.fillStyle = "#475569"; ctx.font = "12px Inter,sans-serif";
    ctx.fillText("Any H vs H̄ spectral difference → CPT violation → Lorentz violation", W / 2, 275);
    ctx.fillText("Also tested: charge-to-mass ratio, gravitational behaviour", W / 2, 295);
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("ALPHA-g (2023): antihydrogen falls DOWN  -  first direct antimatter gravity test", W / 2, 325);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 21.8 – Universality of Physical Laws Across Particle Species */
export const UniversalityLaws = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 340;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Universality  -  Same Laws for All Particles", W / 2, 24);

    // Central concept
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(150, 45, 260, 50, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(150, 45, 260, 50, 10); ctx.stroke();
    ctx.fillStyle = "#1E40AF"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Equivalence Principle + Lorentz Inv.", W / 2, 68);
    ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Physics is independent of particle species", W / 2, 85);

    // Test categories as rays
    const cats = [
      { label: "Lepton universality", desc: "e, μ, τ couple equally to W/Z", bound: "< 10⁻³", color: "#EF4444" },
      { label: "Quark universality", desc: "Same strong coupling for all flavours", bound: "αs universal", color: "#3B82F6" },
      { label: "Gravitational", desc: "All particles fall the same way", bound: "η < 10⁻¹³", color: "#10B981" },
      { label: "Clock comparison", desc: "Cs vs Rb vs H transition rates", bound: "drift < 10⁻¹⁶/yr", color: "#8B5CF6" },
    ];
    cats.forEach((ca, i) => {
      const y = 120 + i * 52;
      ctx.fillStyle = ca.color + "18";
      ctx.beginPath(); ctx.roundRect(30, y, 500, 42, 8); ctx.fill();
      ctx.strokeStyle = ca.color; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.roundRect(30, y, 500, 42, 8); ctx.stroke();
      ctx.fillStyle = ca.color; ctx.font = "bold 12px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(ca.label, 45, y + 17);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(ca.desc, 45, y + 33);
      ctx.fillStyle = "#059669"; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "right"; ctx.fillText(ca.bound, 520, y + 25);
    });
    ctx.textAlign = "center";

    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("BL4S relevance: measuring π/K/p time-of-flight tests universality of kinematics", W / 2, 335);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

// ============================================
// Chapter 22  -  Transition Radiation (TR)
// ============================================

/** 22.1 – Theory of Transition Radiation */
export const TransitionRadiationTheory = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 340;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Transition Radiation  -  Theory", W / 2, 24);

    // Boundary crossing diagram
    ctx.fillStyle = "#DBEAFE";
    ctx.fillRect(30, 60, 220, 160);
    ctx.fillStyle = "#EDE9FE";
    ctx.fillRect(250, 60, 220, 160);
    ctx.strokeStyle = "#F59E0B"; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(250, 55); ctx.lineTo(250, 225); ctx.stroke();
    ctx.fillStyle = "#3B82F6"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("Medium 1 (ε₁)", 140, 80);
    ctx.fillStyle = "#8B5CF6";
    ctx.fillText("Medium 2 (ε₂)", 370, 80);
    ctx.fillStyle = "#F59E0B"; ctx.font = "bold 11px Inter,sans-serif";
    ctx.fillText("Boundary", 250, 50);

    // Particle traversing boundary
    ctx.strokeStyle = "#EF4444"; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(60, 140); ctx.lineTo(440, 140); ctx.stroke();
    ctx.fillStyle = "#EF4444";
    ctx.beginPath(); ctx.moveTo(440, 140); ctx.lineTo(433, 135); ctx.lineTo(433, 145); ctx.closePath(); ctx.fill();
    ctx.font = "bold 11px Inter,sans-serif"; ctx.textAlign = "left";
    ctx.fillText("γ ≫ 1 particle", 445, 143);
    ctx.textAlign = "center";

    // TR photons emitted forward
    ctx.strokeStyle = "#F59E0B"; ctx.lineWidth = 1.5;
    for (let a = -30; a <= 30; a += 10) {
      const rad = (a * Math.PI) / 180;
      ctx.beginPath();
      ctx.moveTo(250, 140);
      ctx.lineTo(250 + 80 * Math.cos(rad), 140 - 80 * Math.sin(rad));
      ctx.stroke();
    }
    ctx.fillStyle = "#F59E0B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("TR X-rays (θ ~ 1/γ)", 340, 100);

    // Key formulae
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Key Properties", W / 2, 250);
    ctx.font = "12px Inter,sans-serif"; ctx.fillStyle = "#475569";
    ctx.fillText("Energy per crossing: ∝ γ  (proportional to Lorentz factor)", W / 2, 270);
    ctx.fillText("Emission angle: θ ~ 1/γ  (forward cone)", W / 2, 290);
    ctx.fillText("Photon energy: soft X-rays (keV range)", W / 2, 310);
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Predicted by Ginzburg & Frank (1946)  -  observed 1959", W / 2, 335);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 22.2 – X-Ray Production and Spectral Properties */
export const XRayProduction = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 340;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("TR X-Ray Spectrum", W / 2, 24);

    // Spectrum plot
    const plotL = 80, plotR = 480, plotT = 55, plotB = 220;
    ctx.strokeStyle = "#94A3B8"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(plotL, plotB); ctx.lineTo(plotR, plotB); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(plotL, plotB); ctx.lineTo(plotL, plotT); ctx.stroke();
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Photon energy ω (keV)", (plotL + plotR) / 2, plotB + 20);
    ctx.save(); ctx.translate(plotL - 20, (plotT + plotB) / 2); ctx.rotate(-Math.PI / 2);
    ctx.fillText("dN/dω", 0, 0); ctx.restore();

    // Spectrum curve (rises, peaks, falls)
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2.5;
    ctx.beginPath();
    for (let i = 0; i <= 200; i++) {
      const frac = i / 200;
      const omega = frac * 30; // keV
      const intensity = omega * Math.exp(-omega / 6) * 12;
      const x = plotL + frac * (plotR - plotL);
      const y = plotB - intensity;
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.stroke();

    // Peak marker
    ctx.strokeStyle = "#EF4444"; ctx.lineWidth = 1; ctx.setLineDash([4, 3]);
    const peakX = plotL + (6 / 30) * (plotR - plotL);
    ctx.beginPath(); ctx.moveTo(peakX, plotB); ctx.lineTo(peakX, plotT + 20); ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = "#EF4444"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("ω_peak ≈ γ ω_p", peakX + 40, plotT + 30);

    // Characteristic energy labels
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("Spectrum Features", W / 2, 255);
    const feats = [
      { label: "ω_p (plasma freq)", val: "~20 eV for typical foils", color: "#3B82F6" },
      { label: "ω_peak", val: "≈ γ · ω_p / 3", color: "#EF4444" },
      { label: "N_photons/crossing", val: "≈ α/π ≈ 0.5% (fine structure)", color: "#8B5CF6" },
    ];
    ctx.font = "12px Inter,sans-serif";
    feats.forEach((f, i) => {
      ctx.fillStyle = f.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(f.label, 80, 275 + i * 20);
      ctx.fillStyle = "#475569"; ctx.font = "12px Inter,sans-serif";
      ctx.fillText(f.val, 250, 275 + i * 20);
    });
    ctx.textAlign = "center";
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Need many foils (stack) to accumulate detectable X-ray signal", W / 2, 335);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 22.3 – Foil Stack Radiators and Design Optimization */
export const FoilStackRadiator = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 340;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Foil Stack Radiator Design", W / 2, 24);

    // Stack of foils
    const nFoils = 12;
    for (let i = 0; i < nFoils; i++) {
      const x = 60 + i * 32;
      // Foil
      ctx.fillStyle = "#94A3B8";
      ctx.fillRect(x, 60, 4, 120);
      // Gap
      if (i < nFoils - 1) {
        ctx.fillStyle = "#DBEAFE";
        ctx.fillRect(x + 4, 60, 28, 120);
      }
    }
    // Labels
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 11px Inter,sans-serif";
    ctx.fillText("← Foil stack (N ~ 100–1000 foils) →", 250, 55);
    ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("Foil: 10–25 μm", 460, 90);
    ctx.fillText("(Li, CH₂, Mylar)", 460, 105);
    ctx.fillText("Gap: 100–300 μm", 460, 130);
    ctx.fillText("(air or vacuum)", 460, 145);
    ctx.textAlign = "center";

    // Particle arrow through stack
    ctx.strokeStyle = "#EF4444"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(30, 120); ctx.lineTo(450, 120); ctx.stroke();
    ctx.fillStyle = "#EF4444";
    ctx.beginPath(); ctx.moveTo(450, 120); ctx.lineTo(443, 115); ctx.lineTo(443, 125); ctx.closePath(); ctx.fill();

    // Design parameters
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Optimization Parameters", W / 2, 205);
    const params = [
      { par: "Foil thickness", opt: "≈ 10–25 μm  -  balance absorption vs yield", color: "#3B82F6" },
      { par: "Gap spacing", opt: "Tuned for constructive interference (formation zone)", color: "#8B5CF6" },
      { par: "Number of foils", opt: "N > 100 typical  -  more foils → more photons", color: "#10B981" },
      { par: "Material Z", opt: "Low-Z preferred (less absorption): Li, polypropylene", color: "#F59E0B" },
    ];
    ctx.font = "12px Inter,sans-serif";
    params.forEach((p, i) => {
      ctx.fillStyle = p.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(p.par, 50, 225 + i * 22);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(p.opt, 175, 225 + i * 22);
    });
    ctx.textAlign = "center";
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Formation zone length: l_f = 2c/ω_p · γ  -  sets optimal gap", W / 2, 325);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 22.4 – TRD for Electron/Pion Separation at BL4S */
export const TRDElectronPion = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 340;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("TRD for e/π Separation at BL4S", W / 2, 24);

    // Two-particle comparison
    const parts = [
      { name: "Electron (e⁻)", mass: "0.511 MeV", gamma: "γ ≈ 2000", tr: "Yes  -  strong TR", color: "#3B82F6", bg: "#DBEAFE", y: 50 },
      { name: "Pion (π⁻)", mass: "139.6 MeV", gamma: "γ ≈ 7", tr: "No  -  below threshold", color: "#EF4444", bg: "#FEE2E2", y: 120 },
    ];
    parts.forEach((p) => {
      ctx.fillStyle = p.bg;
      ctx.beginPath(); ctx.roundRect(30, p.y, 500, 55, 10); ctx.fill();
      ctx.strokeStyle = p.color; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.roundRect(30, p.y, 500, 55, 10); ctx.stroke();
      ctx.fillStyle = p.color; ctx.font = "bold 12px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(p.name, 45, p.y + 20);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(`m = ${p.mass}`, 45, p.y + 38);
      ctx.fillText(p.gamma, 200, p.y + 28);
      ctx.fillStyle = p.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "right"; ctx.fillText(p.tr, 520, p.y + 28);
    });
    ctx.textAlign = "center";

    // Detection scheme
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Detection Scheme", W / 2, 200);

    // Radiator → Xe gas → signal
    const blocks = [
      { label: "Foil\nradiator", x: 60, color: "#94A3B8" },
      { label: "Xe/CO₂\ngas", x: 200, color: "#3B82F6" },
      { label: "MWPC\nreadout", x: 340, color: "#10B981" },
    ];
    blocks.forEach((b) => {
      ctx.fillStyle = b.color + "20";
      ctx.beginPath(); ctx.roundRect(b.x, 215, 110, 55, 8); ctx.fill();
      ctx.strokeStyle = b.color; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.roundRect(b.x, 215, 110, 55, 8); ctx.stroke();
      ctx.fillStyle = b.color; ctx.font = "bold 11px Inter,sans-serif";
      b.label.split("\n").forEach((ln, i) => ctx.fillText(ln, b.x + 55, 237 + i * 14));
    });
    // Arrows between blocks
    [170, 310].forEach((x) => {
      ctx.strokeStyle = "#94A3B8"; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.moveTo(x, 243); ctx.lineTo(x + 30, 243); ctx.stroke();
    });

    ctx.fillStyle = "#475569"; ctx.font = "12px Inter,sans-serif";
    ctx.fillText("Electron: dE/dx + TR X-rays → large signal in Xe gas", W / 2, 295);
    ctx.fillText("Pion: dE/dx only → smaller signal → separation achieved", W / 2, 315);
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Rejection factor: ~100:1 for π at 90% e efficiency", W / 2, 335);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 22.5 – TRDs in Modern Experiments */
export const TRDModernExperiments = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 340;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("TRDs in Major Experiments", W / 2, 24);

    const exps = [
      { name: "ALICE TRD", loc: "LHC, CERN", purpose: "e/π separation at p > 1 GeV/c", foils: "Rohacell foam", layers: "6 layers, 540 chambers", color: "#3B82F6" },
      { name: "AMS-02 TRD", loc: "ISS (Space)", purpose: "e⁺/p separation in cosmic rays", foils: "Fleece radiator", layers: "20 layers, 328 modules", color: "#8B5CF6" },
      { name: "ATLAS TRT", loc: "LHC, CERN", purpose: "Electron ID + tracking", foils: "Polypropylene fibres/foils", layers: "~300k straw tubes", color: "#10B981" },
      { name: "CBM TRD", loc: "FAIR, GSI", purpose: "e/π in heavy-ion collisions", foils: "Irregular radiator", layers: "4 stations planned", color: "#F59E0B" },
    ];
    exps.forEach((e, i) => {
      const y = 45 + i * 70;
      ctx.fillStyle = e.color + "12";
      ctx.beginPath(); ctx.roundRect(20, y, 520, 60, 10); ctx.fill();
      ctx.strokeStyle = e.color; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.roundRect(20, y, 520, 60, 10); ctx.stroke();
      ctx.fillStyle = e.color; ctx.font = "bold 12px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(e.name, 35, y + 18);
      ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(e.loc, 155, y + 18);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(e.purpose, 35, y + 36);
      ctx.fillText(`${e.foils}  |  ${e.layers}`, 35, y + 52);
    });
    ctx.textAlign = "center";
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("All exploit γ-dependence of TR to distinguish light from heavy particles", W / 2, 335);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 22.6 – Gamma Identification and Particle Separation Capabilities */
export const GammaIdentification = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 340;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Particle Separation via Lorentz Factor γ", W / 2, 24);

    // Threshold diagram: γ_threshold vs particle mass
    const plotL = 90, plotR = 510, plotT = 55, plotB = 210;
    ctx.strokeStyle = "#94A3B8"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(plotL, plotB); ctx.lineTo(plotR, plotB); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(plotL, plotB); ctx.lineTo(plotL, plotT); ctx.stroke();
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Particle momentum (GeV/c)", (plotL + plotR) / 2, plotB + 18);
    ctx.save(); ctx.translate(plotL - 25, (plotT + plotB) / 2); ctx.rotate(-Math.PI / 2);
    ctx.fillText("γ factor", 0, 0); ctx.restore();

    // Particle γ curves (γ = p/m)
    const particles = [
      { name: "e⁻", mass: 0.000511, color: "#3B82F6" },
      { name: "π", mass: 0.1396, color: "#EF4444" },
      { name: "K", mass: 0.4937, color: "#10B981" },
      { name: "p", mass: 0.9383, color: "#8B5CF6" },
    ];
    const maxP = 5; // GeV/c
    const maxGamma = 300;
    particles.forEach((p) => {
      ctx.strokeStyle = p.color; ctx.lineWidth = 2;
      ctx.beginPath();
      for (let i = 0; i <= 100; i++) {
        const mom = (i / 100) * maxP;
        const gamma = Math.sqrt(1 + (mom / p.mass) ** 2);
        const x = plotL + (mom / maxP) * (plotR - plotL);
        const y = plotB - (Math.min(gamma, maxGamma) / maxGamma) * (plotB - plotT);
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.stroke();
    });

    // Legend
    particles.forEach((p, i) => {
      const lx = 120 + i * 105;
      ctx.fillStyle = p.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.fillText(p.name, lx, plotB + 35);
      ctx.strokeStyle = p.color; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(lx - 18, plotB + 32); ctx.lineTo(lx - 5, plotB + 32); ctx.stroke();
    });

    // TR threshold line
    ctx.strokeStyle = "#F59E0B"; ctx.lineWidth = 1.5; ctx.setLineDash([6, 4]);
    const thrY = plotB - (1000 / maxGamma) * (plotB - plotT);
    const clampedY = Math.max(plotT, thrY);
    ctx.beginPath(); ctx.moveTo(plotL, plotT + 20); ctx.lineTo(plotR, plotT + 20); ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = "#F59E0B"; ctx.font = "bold 11px Inter,sans-serif";
    ctx.textAlign = "right"; ctx.fillText("γ_th ~ 1000 (TR onset)", plotR, plotT + 15);
    ctx.textAlign = "center";

    // Explanation
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Separation Capability", W / 2, 265);
    ctx.font = "12px Inter,sans-serif"; ctx.fillStyle = "#475569";
    ctx.fillText("At same momentum, lighter particles have larger γ → more TR", W / 2, 285);
    ctx.fillText("e reaches γ_th at much lower p than π, K, or p", W / 2, 305);
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Combined with dE/dx and TOF for full PID capability", W / 2, 330);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 22.10 – TRD Design at ALICE and AMS-02: Comparative Analysis */
export const ALICEvsAMS = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("ALICE TRD vs AMS-02 TRD  -  Comparison", W / 2, 24);

    // Two columns
    const cols = [
      {
        title: "ALICE TRD", color: "#3B82F6", bg: "#DBEAFE", x: 20, w: 250,
        rows: [
          ["Location", "LHC Point 2, CERN"],
          ["Goal", "e/π at p > 1 GeV/c"],
          ["Radiator", "Rohacell foam (irregular)"],
          ["Gas", "Xe/CO₂ (85/15)"],
          ["Readout", "Drift chambers (MWPC)"],
          ["Layers", "6 layers, 18 supermodules"],
          ["Rejection", "~100:1 π rejection"],
        ],
      },
      {
        title: "AMS-02 TRD", color: "#8B5CF6", bg: "#EDE9FE", x: 290, w: 250,
        rows: [
          ["Location", "ISS (400 km orbit)"],
          ["Goal", "e⁺/p in cosmic rays"],
          ["Radiator", "Fleece fibre blankets"],
          ["Gas", "Xe/CO₂ (80/20)"],
          ["Readout", "Straw tubes (proportional)"],
          ["Layers", "20 layers, 328 modules"],
          ["Rejection", "~1000:1 p rejection"],
        ],
      },
    ];
    cols.forEach((col) => {
      ctx.fillStyle = col.bg;
      ctx.beginPath(); ctx.roundRect(col.x, 45, col.w, 260, 10); ctx.fill();
      ctx.strokeStyle = col.color; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.roundRect(col.x, 45, col.w, 260, 10); ctx.stroke();
      ctx.fillStyle = col.color; ctx.font = "bold 13px Inter,sans-serif";
      ctx.fillText(col.title, col.x + col.w / 2, 68);
      col.rows.forEach((r, i) => {
        const y = 90 + i * 30;
        ctx.fillStyle = col.color; ctx.font = "bold 11px Inter,sans-serif";
        ctx.textAlign = "left"; ctx.fillText(r[0], col.x + 15, y);
        ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
        ctx.fillText(r[1], col.x + 15, y + 14);
      });
    });
    ctx.textAlign = "center";

    // Common principles
    ctx.fillStyle = "#FEF3C7";
    ctx.beginPath(); ctx.roundRect(60, 315, 440, 35, 8); ctx.fill();
    ctx.strokeStyle = "#F59E0B"; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.roundRect(60, 315, 440, 35, 8); ctx.stroke();
    ctx.fillStyle = "#92400E"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("Both use Xe gas for efficient X-ray absorption (Z=54, high σ_photo)", W / 2, 337);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};
